import type { GenerationImpactScenarioSnapshot } from '../../types/generationImpact';
import type { ProjectIntent } from '../../types/siteRag';
import type { GenerationAssessmentSelection } from '../../types/gridLedStandbyPlanning';
import type { GenerationProjectInputBinding } from '../../types/generationProjectInputSync';
import { generationAssessmentSelectionSchema } from './gridLedStandbyPlanningIntegrity';
import { assertFirestoreNestedArraysDecoded } from './firestoreNestedArrayCodec';
import { generationScenarioProjectInputStalenessReason } from './generationProjectInputSync';

/**
 * Resolve the immutable promoted generation snapshot and enforce the integrity
 * stamps captured in Project Intent. New sites prefer the site-embedded copy;
 * the scenario subcollection remains a legacy fallback.
 */
export function resolvePromotedGenerationScenario(
  intent: ProjectIntent | null | undefined,
  embedded: GenerationImpactScenarioSnapshot | null | undefined,
  fetched: GenerationImpactScenarioSnapshot | null | undefined,
  selection?: GenerationAssessmentSelection,
  projectInputBinding?: GenerationProjectInputBinding,
): GenerationImpactScenarioSnapshot | null {
  let selected: GenerationAssessmentSelection | undefined;
  if (selection !== undefined) {
    const parsed = generationAssessmentSelectionSchema.safeParse(selection);
    if (!parsed.success || parsed.data.kind !== 'generation-impact-v1') return null;
    selected = parsed.data;
  }
  const promotedId = intent?.promotedGenerationScenarioId;
  if (!promotedId) return null;
  const matches = (
    scenario: GenerationImpactScenarioSnapshot | null | undefined,
    source: 'site-embedded' | 'scenario-document',
  ): scenario is GenerationImpactScenarioSnapshot => {
    if (!scenario || scenario.id !== promotedId) return false;
    if (generationScenarioProjectInputStalenessReason(intent, scenario, projectInputBinding)) return false;
    // Compare-only resolver: it reads STORED hash scalars and never recomputes,
    // so an un-decoded body would pass every check here and land in the SED.
    // The decode belongs at the Firestore read boundary; this only tells a
    // developer when one is missing, and is inert in a deployed process — this
    // function must stay total for its live callers (the Path to GO decision
    // routes, the executive rollup useMemo).
    assertFirestoreNestedArraysDecoded(
      `Promoted generation scenario (${source})`,
      scenario.input,
      scenario.result,
    );
    if (intent.promotedGenerationInputHash && scenario.inputHash !== intent.promotedGenerationInputHash) return false;
    if (intent.promotedGenerationResultHash && scenario.resultHash !== intent.promotedGenerationResultHash) return false;
    if (selected && (scenario.id !== selected.assessmentId || scenario.inputHash !== selected.inputHash
      || scenario.resultHash !== selected.resultHash || scenario.promotedAt !== selected.selectedAt)) return false;
    return true;
  };
  if (matches(embedded, 'site-embedded')) return embedded;
  if (matches(fetched, 'scenario-document')) return fetched;
  return null;
}
